import { apiErrors, apiMethodnames, USERNAME_TYPE } from '../constants/common';
import { AMEX, DISCOVER, MA, VISA } from '../constants/partnering-networks';
import handleIdentityLookupPreference from '../handlers/handle-identity-lookup-preference';
import collapsedLogger from '../helpers/collapsed-logger';
import getNetworkInstance from '../helpers/get-network-instance';
import { trackIdentityLookup } from '../helpers/gtm';
import OrchCustomData from '../helpers/orch-custom-data';
import promiseTimeout from '../helpers/promise-timeout';

const identityLookup = (data = {}) => {
  return new Promise(resolve => {
    let gtmTracked = false;
    let vsbGtmData;
    const timeOutError = network => ({
      [network]: {
        error: apiErrors.TIME_OUT
      }
    });
    const promiseRejectError = network => ({
      [network]: {
        error: apiErrors.REJECT
      }
    });

    const promiseWrapper1 = function(instance, instanceName, data, method) {
      return new Promise(resolve => {
        if (instance === null || instance === undefined) {
          const resultData = {
            [instanceName]: {
              error: apiErrors.NO_INSTANCE
            }
          };
          resolve(resultData);
        }
        const vco = getNetworkInstance(OrchCustomData, VISA);

        instance[method](data)
          .then(res => {
            const resultData = {
              [instanceName]: {
                data: res,
                timestamp: Date.now()
              }
            };
            if (!gtmTracked) {
              vsbGtmData = { isError: false, isRequest: false, isVsb: OrchCustomData.isVsb };
              trackIdentityLookup(data, OrchCustomData, vsbGtmData, instanceName);
              gtmTracked = true;
            }
            OrchCustomData.apiTimeStamp.identityLookup[instanceName].endTime = Date.now();
            OrchCustomData.apiTimeStamp.identityLookup[instanceName].timeTakenInMillSeconds =
              OrchCustomData.apiTimeStamp.identityLookup[instanceName].endTime -
              OrchCustomData.apiTimeStamp.identityLookup[instanceName].startTime;
            const step = `ORCH_IDENTITY_LOOKUP_${instanceName}_API_SUCCESS_RESPONSE`;
            if (vco && typeof vco.pushEvents === 'function') {
              vco.pushEvents({
                E: step,
                ERRCODE: null,
                ERRMSG: null,
                ETIME: OrchCustomData.apiTimeStamp.identityLookup[instanceName].endTime,
                LL: 'INFO',
                S: 200,
                STIME: OrchCustomData.apiTimeStamp.identityLookup[instanceName].startTime,
                TY: 'RESPONSE',
                checkout_step: step,
                response: res,
                srcInitiatorId: OrchCustomData.srcInitiatorId[instanceName]
              });
            }
            resolve(resultData);
          })
          .catch(error => {
            if (!gtmTracked) {
              vsbGtmData = { isError: true, isRequest: false, isVsb: OrchCustomData.isVsb };
              trackIdentityLookup(data, OrchCustomData, vsbGtmData, instanceName);
              gtmTracked = true;
            }
            const resultData = {
              [instanceName]: error
            };
            OrchCustomData.apiTimeStamp.identityLookup[instanceName].endTime = Date.now();
            OrchCustomData.apiTimeStamp.identityLookup[instanceName].timeTakenInMillSeconds =
              OrchCustomData.apiTimeStamp.identityLookup[instanceName].endTime -
              OrchCustomData.apiTimeStamp.identityLookup[instanceName].startTime;
            const stepErr = `ORCH_IDENTITY_LOOKUP_${instanceName}_API_ERROR_RESPONSE`;
            if (vco && typeof vco.pushEvents === 'function') {
              vco.pushEvents({
                E: stepErr,
                ERRCODE: error && error.error_code ? error.err_code : '',
                ERRMSG: error && error.reason ? error.reason : '',
                ETIME: OrchCustomData.apiTimeStamp.identityLookup[instanceName].endTime,
                LL: 'INFO',
                S: null,
                STIME: OrchCustomData.apiTimeStamp.identityLookup[instanceName].startTime,
                TY: 'RESPONSE',
                checkout_step: stepErr,
                response: error,
                srcInitiatorId: OrchCustomData.srcInitiatorId[instanceName]
              });
            }
            resolve(resultData);
          });
      });
    };

    const { IDENTITY_LOOKUP } = apiMethodnames;
    const promiseArr = [];
    let loadedSdk = OrchCustomData.srcSdks.loadedSdk;

    // F11669 - if usernameType exists and is REMEMBER_ME, then only perform identity lookup for Visa.
    // The username will be masked and masked email address lookup is not supported by other networks
    if (data.usernameType === USERNAME_TYPE.REMEMBER_ME) {
      loadedSdk = { VISA: loadedSdk.VISA };
    }

    OrchCustomData.apiTimeStamp.identityLookup = {};

    if (OrchCustomData.isVsb && !OrchCustomData.isVsbButtonless) {
      vsbGtmData = { isError: false, isRequest: true, isVsb: OrchCustomData.isVsb };
      trackIdentityLookup(data, OrchCustomData, vsbGtmData, '');
    }

    for (const key in loadedSdk) {
      let finalData = data;
      const sdkInstance = loadedSdk[key];
      OrchCustomData.apiTimeStamp.identityLookup[key] = {};
      OrchCustomData.apiTimeStamp.identityLookup[key].startTime = Date.now();
      if (key !== VISA) {
        delete data.branding;

        // F11669 - usernameType is used by Visa lookupUserName endpoint to distinguish masked and unmasked email addresses; should not send value to other networks
        delete data.usernameType;
      }

      if (key === MA || key === DISCOVER) {
        const { identityValue } = data;
        finalData = {
          consumerIdentity: {
            identityProvider: 'SRC',
            identityType: 'EMAIL_ADDRESS',
            identityValue
          }
        };
      }

      if (key === VISA) {
        finalData = {
          consumerIdentity: {
            ...data,
            identityProvider: 'SRC'
          }
        };
      }

      if (key === AMEX) {
        finalData = {
          ...data,
          identityProvider: 'SRC',
          type: 'EMAIL'
        };
      }
      const sdkPromise = promiseTimeout(
        promiseWrapper1(sdkInstance, key, finalData, IDENTITY_LOOKUP),
        timeOutError(key),
        promiseRejectError(key)
      );
      collapsedLogger(`${key} identityLookup => `, finalData);
      promiseArr.push(sdkPromise);
    }

    Promise.all(promiseArr).then(values => {
      for (const value of values) {
        OrchCustomData.setIdentityLookup(value);
      }
      resolve(handleIdentityLookupPreference());
    });
  });
};

export default identityLookup;
